import { useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useAuth } from "context/AuthContext";
import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import {
  PortalButton,
  PortalCard,
  PortalGrid,
  PortalGridItem,
  PortalLinkRow,
  PortalLoading,
  PortalMetric,
} from "components/portal/PortalUi";
import { useQuery } from "@tanstack/react-query";
import { get } from "lib/api";
import { useCurrency } from "context/CurrencyContext";
import { useCart } from "context/CartContext";
import { isDistributorRole, isPortalRole } from "lib/portalRoles";
import { countryForCompany } from "lib/companyProfile";
import useStorefrontCopy from "hooks/useStorefrontCopy";

export default function CustomerAccount() {
  const { token, pluginRole, authLoading, activeCompany, storefrontTenant } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useStorefrontCopy();
  const { formatUSDForDisplay } = useCurrency();
  const { itemCount } = useCart();

  const role = String(pluginRole || "").toLowerCase();
  const isPortal = isPortalRole(role);
  const distributor = isDistributorRole(role);

  const meQuery = useQuery({
    queryKey: ["storefront-me"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!token && isPortal,
    staleTime: 15000,
  });

  const ordersQuery = useQuery({
    queryKey: ["storefront-orders", "recent"],
    queryFn: () => get("/api/storefront/orders", { pagination: { page: 1, pageSize: 5 } }),
    enabled: !!token && isPortal,
    staleTime: 15000,
  });

  const requestsQuery = useQuery({
    queryKey: ["storefront-distributor-requests", "recent"],
    queryFn: () => get("/api/storefront/distributor-order-requests", { pagination: { page: 1, pageSize: 5 } }),
    enabled: !!token && distributor,
    staleTime: 15000,
  });

  const customersQuery = useQuery({
    queryKey: ["storefront-distributor-customers", "count"],
    queryFn: () => get("/api/storefront/distributor-customers"),
    enabled: !!token && distributor,
    staleTime: 15000,
  });

  const customer = meQuery.data?.data?.attributes
    ? {
        id: meQuery.data.data.id,
        documentId: meQuery.data.data.documentId || meQuery.data.data.id,
        ...meQuery.data.data.attributes,
      }
    : meQuery.data?.data || null;

  const recentOrders = Array.isArray(ordersQuery.data?.data) ? ordersQuery.data.data : [];
  const recentRequests = Array.isArray(requestsQuery.data?.data) ? requestsQuery.data.data : [];
  const linkedCustomers = Array.isArray(customersQuery.data?.data) ? customersQuery.data.data : [];

  const isMx = countryForCompany(storefrontTenant || activeCompany) === "MX";
  const needsSat =
    !distributor &&
    isMx &&
    !!customer?.wantsFacturaMxByDefault &&
    (!customer?.rfc || !customer?.regimenFiscal || !customer?.usoCfdi || !customer?.fiscalZip);
  const addressCount = Array.isArray(customer?.addresses) ? customer.addresses.length : 0;

  const welcome = useMemo(() => {
    const name = customer?.name;
    if (name) {
      return t("portal.welcomeBackName", "Welcome back, {name}.").replace("{name}", name);
    }
    return t("portal.welcomeBack", "Welcome back.");
  }, [customer?.name, t]);

  if (!token) {
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  }
  if (authLoading || pluginRole == null) return null;
  if (!isPortal) return <Navigate to="/dashboard" replace />;

  return (
    <CustomerPortalLayout
      title={distributor ? t("portal.distributorHubTitle", "Distributor account") : t("portal.accountHome", "Account")}
      subtitle={welcome}
      actions={
        <PortalButton variant="ghost" startIcon="shopping_bag" onClick={() => navigate("/catalog")}>
          {t("portal.continueShopping", "Continue shopping")}
        </PortalButton>
      }
    >
      <PortalGrid spacing={2}>
        {distributor ? (
          <>
            <PortalGridItem xs={6} md={3}>
              <PortalMetric
                icon="rule"
                label={t("portal.distributorRequests", "Order requests")}
                value={requestsQuery.isLoading ? "…" : recentRequests.length}
                hint={t("portal.distributorRequestsDetail", "Approve requests or send them to VBS.")}
              />
            </PortalGridItem>
            <PortalGridItem xs={6} md={3}>
              <PortalMetric
                icon="groups"
                label={t("portal.linkedCustomers", "Linked customers")}
                value={customersQuery.isLoading ? "…" : linkedCustomers.length}
                hint={t("portal.linkedCustomersDetail", "Customers on your distributor account.")}
              />
            </PortalGridItem>
            <PortalGridItem xs={6} md={3}>
              <PortalMetric
                icon="receipt_long"
                label={t("orders.recentOrders", "Recent orders")}
                value={ordersQuery.isLoading ? "…" : recentOrders.length}
                hint={t("portal.ordersHint", "Track status and invoices")}
              />
            </PortalGridItem>
            <PortalGridItem xs={6} md={3}>
              <PortalMetric
                icon="shopping_cart"
                label={t("cart.title", "Cart")}
                value={itemCount || 0}
                hint={t("portal.cartHint", "Items ready to order")}
              />
            </PortalGridItem>
          </>
        ) : (
          <>
            <PortalGridItem xs={6} md={3}>
              <PortalMetric
                icon="receipt_long"
                label={t("orders.recentOrders", "Recent orders")}
                value={ordersQuery.isLoading ? "…" : recentOrders.length}
                hint={t("portal.ordersHint", "Track status and invoices")}
              />
            </PortalGridItem>
            <PortalGridItem xs={6} md={3}>
              <PortalMetric
                icon="location_on"
                label={t("addresses.title", "Addresses")}
                value={addressCount}
                hint={t("portal.addressesHint", "Saved for checkout")}
              />
            </PortalGridItem>
            <PortalGridItem xs={6} md={3}>
              <PortalMetric
                icon="shopping_cart"
                label={t("cart.title", "Cart")}
                value={itemCount || 0}
                hint={t("portal.cartHint", "Items ready to order")}
              />
            </PortalGridItem>
            <PortalGridItem xs={6} md={3}>
              <PortalMetric
                icon="qr_code_2"
                label={t("registeredUnits.title", "Registered Items")}
                value={needsSat ? "!" : "→"}
                hint={
                  needsSat
                    ? t("profile.missingTaxFields", "Missing tax fields for checkout")
                    : t("registeredUnits.subtitle", "Warranty and core returns")
                }
              />
            </PortalGridItem>
          </>
        )}

        {distributor ? (
          <PortalGridItem xs={12} md={6}>
            <PortalCard
              title={t("portal.distributorRequests", "Order requests")}
              subtitle={t("portal.distributorRequestsDetail", "Approve requests or send them to VBS.")}
            >
              {requestsQuery.isLoading ? (
                <PortalLoading label={t("common.loading", "Loading…")} />
              ) : recentRequests.length ? (
                <Stack spacing={0.75}>
                  {recentRequests.map((req) => {
                    const id = req?.documentId || req?.id;
                    const label = req?.requestNumber || req?.customerName || id;
                    return (
                      <PortalLinkRow
                        key={String(id)}
                        title={label}
                        meta={String(req?.status || "").replaceAll("_", " ") || "—"}
                        onClick={() => navigate(`/account/distributor-requests/${id}`)}
                      />
                    );
                  })}
                </Stack>
              ) : (
                <Box component="p" sx={{ m: 0, color: "#5C6370", fontSize: "0.875rem" }}>
                  {t("portal.noRequests", "No order requests yet.")}
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <PortalButton
                  variant="primary"
                  endIcon="chevron_right"
                  onClick={() => navigate("/account/distributor-requests")}
                >
                  {t("portal.viewAllRequests", "View all requests")}
                </PortalButton>
              </Box>
            </PortalCard>
          </PortalGridItem>
        ) : null}

        <PortalGridItem xs={12} md={6}>
          <PortalCard
            title={t("orders.recentOrders", "Recent orders")}
            subtitle={t("orders.subtitle", "Track orders and documents.")}
          >
            {ordersQuery.isLoading ? (
              <PortalLoading label={t("common.loading", "Loading…")} />
            ) : recentOrders.length ? (
              <Stack spacing={0.75}>
                {recentOrders.map((o) => {
                  const id = o?.documentId || o?.id;
                  const num = o?.salesOrderNumber || id;
                  const total = o?.total != null ? formatUSDForDisplay(Number(o.total)) : "—";
                  return (
                    <PortalLinkRow
                      key={String(id)}
                      title={num}
                      meta={total}
                      onClick={() => navigate(`/account/orders/${id}`)}
                    />
                  );
                })}
              </Stack>
            ) : (
              <Box component="p" sx={{ m: 0, color: "#5C6370", fontSize: "0.875rem" }}>
                {t("orders.noOrders", "No orders yet.")}
              </Box>
            )}
            <Box sx={{ mt: 2 }}>
              <PortalButton variant="primary" endIcon="chevron_right" onClick={() => navigate("/account/orders")}>
                {t("orders.viewAll", "View all orders")}
              </PortalButton>
            </Box>
          </PortalCard>
        </PortalGridItem>

        <PortalGridItem xs={12} md={6}>
          <PortalCard
            title={t("registeredUnits.title", "Registered Items")}
            subtitle={t(
              "registeredUnits.subtitle",
              "Track registered products, returns, and warranty status.",
            )}
          >
            <PortalButton variant="primary" endIcon="chevron_right" onClick={() => navigate("/account/valve-bodies")}>
              {t("registeredUnits.open", "Open registered items")}
            </PortalButton>
          </PortalCard>
        </PortalGridItem>

        <PortalGridItem xs={12} md={6}>
          <PortalCard
            title={distributor ? t("portal.profile", "Profile") : t("portal.profileBilling", "Profile / Billing")}
            subtitle={t("profile.subtitle", "Account and invoice preferences.")}
          >
            {!distributor && needsSat ? (
              <Box
                sx={{
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 1,
                  border: "1px solid rgba(255,167,38,0.45)",
                  bgcolor: "rgba(255,167,38,0.1)",
                  fontSize: "0.8125rem",
                }}
              >
                {t("profile.missingTaxFields", "Missing required tax fields for checkout:")}
              </Box>
            ) : null}
            <PortalButton variant="primary" endIcon="chevron_right" onClick={() => navigate("/account/profile")}>
              {t("portal.openProfile", "Open profile")}
            </PortalButton>
          </PortalCard>
        </PortalGridItem>
      </PortalGrid>
    </CustomerPortalLayout>
  );
}
